import React, { useState, useEffect, useCallback } from "react";
import api from "./api";
import { Search } from "lucide-react";

function ProtectedPage() {
  // If a "user" somehow lands here, bounce them to the user dashboard.
  if (localStorage.getItem("role") === "user") {
    window.location.href = "/user";
    return null;
  }

  const isStaff = localStorage.getItem("role") === "staff";

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMessages, setFilteredMessages] = useState([]);

  // State for toggling admin panel (only useful for staff)
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  const filterMessages = useCallback((term, messageList) => {
    if (!term.trim()) {
      return messageList;
    }
    const searchTermLower = term.toLowerCase();
    return messageList.filter(
      (msg) =>
        msg.message?.toLowerCase().includes(searchTermLower) ||
        msg.sender?.toLowerCase().includes(searchTermLower) ||
        msg.receiver?.toLowerCase().includes(searchTermLower) ||
        msg.port?.assigned_number?.toLowerCase().includes(searchTermLower) ||
        msg.port?.user_code?.toLowerCase().includes(searchTermLower) ||
        msg.port?.user_email?.toLowerCase().includes(searchTermLower) ||
        msg.port?.gateway?.name?.toLowerCase().includes(searchTermLower) ||
        String(msg.gateway_id || "")
          .toLowerCase()
          .includes(searchTermLower)
    );
  }, []);

  // Fetch initial messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await api.get("/received_sms/");
        setMessages(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching messages:", error);
        setError("Failed to load messages");
        setLoading(false);
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          window.location.href = "/login";
        }
      }
    };
    fetchMessages();
  }, []);

  // Handle WebSocket connection
  useEffect(() => {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${protocol}://${window.location.host}/ws`);

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.new_message) {
        setMessages((prevMessages) => {
          const newMessage = {
            ...data.new_message,
            port: data.new_message.port_info
              ? {
                  assigned_number: data.new_message.port_info.assigned_number,
                  user_code: data.new_message.port_info.user_code,
                  user_email: data.new_message.port_info.user_email,
                  port_number: data.new_message.port_info.port_number,
                  gateway_id: data.new_message.port_info.gateway_id,
                  gateway: data.new_message.port_info.gateway_name
                    ? { name: data.new_message.port_info.gateway_name }
                    : undefined,
                }
              : null,
          };
          return [newMessage, ...prevMessages];
        });
      }
    };

    ws.onopen = () => console.log("WebSocket connection established");
    ws.onclose = () => console.log("WebSocket connection closed");
    ws.onerror = (error) => console.error("WebSocket error:", error);

    return () => ws.close();
  }, []);

  // Update filtered messages when messages or search term changes
  useEffect(() => {
    const filtered = filterMessages(searchTerm, messages);
    setFilteredMessages(filtered);
  }, [messages, searchTerm, filterMessages]);

  // Toggle admin panel
  const handleAdminToggle = () => {
    setShowAdminPanel(!showAdminPanel);
  };

  // Example: Admin create user form handler (pseudo-code)
  const handleCreateUser = async (e) => {
    e.preventDefault();
    const username = e.target.username.value.trim();
    const password = e.target.password.value.trim();
    try {
      await api.post("/admin_create_user", { username, password });
      alert(`User '${username}' created successfully.`);
    } catch (err) {
      console.error("Error creating user:", err);
      alert(err.response?.data?.detail || "Create user failed");
    }
  };

  // Example: Admin change password for another user (pseudo-code)
  const handleAdminChangePassword = async (e) => {
    e.preventDefault();
    const targetUsername = e.target.targetUsername.value.trim();
    const newPassword = e.target.newPassword.value.trim();
    const confirmPassword = e.target.confirmPassword.value.trim();
    try {
      await api.put("/admin_change_password", {
        username: targetUsername,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      alert(`Password changed for '${targetUsername}'`);
    } catch (err) {
      console.error("Error changing password:", err);
      alert(err.response?.data?.detail || "Change password failed");
    }
  };

  return (
    <div className="container mt-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold">Received Messages</h2>
        <div className="flex items-center gap-4">
          <div className="relative w-96">
            <input
              type="text"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          {/* Admin Panel Toggle Button — only show for staff */}
          {isStaff && (
            <button className="btn btn-secondary" onClick={handleAdminToggle}>
              {showAdminPanel ? "Hide Admin Panel" : "Show Admin Panel"}
            </button>
          )}
        </div>
      </div>

      {/* Admin Panel Section (only visible if toggled & staff) */}
      {isStaff && showAdminPanel && (
        <div className="border p-4 mb-4">
          <h3 className="text-xl font-semibold mb-3">Admin Panel</h3>

          {/* Admin Create User Form */}
          <form onSubmit={handleCreateUser} className="mb-4">
            <div className="mb-2">
              <label>Username:</label>
              <input
                type="text"
                name="username"
                className="form-control"
                placeholder="New username"
                required
              />
            </div>
            <div className="mb-2">
              <label>Password:</label>
              <input
                type="password"
                name="password"
                className="form-control"
                placeholder="New user password"
                required
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Create User
            </button>
          </form>

          {/* Admin Change Another User's Password */}
          <form onSubmit={handleAdminChangePassword}>
            <div className="mb-2">
              <label>Target Username:</label>
              <input
                type="text"
                name="targetUsername"
                className="form-control"
                placeholder="Existing username"
                required
              />
            </div>
            <div className="mb-2">
              <label>New Password:</label>
              <input
                type="password"
                name="newPassword"
                className="form-control"
                placeholder="Enter new password"
                required
              />
            </div>
            <div className="mb-2">
              <label>Confirm Password:</label>
              <input
                type="password"
                name="confirmPassword"
                className="form-control"
                placeholder="Confirm new password"
                required
              />
            </div>
            <button type="submit" className="btn btn-warning">
              Change Password
            </button>
          </form>
        </div>
      )}

      {loading && <div>Loading...</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && (
        <div className="table-responsive">
          <table className="table table-striped table-bordered">
            <thead>
              <tr>
                <th className="w-40">Time</th>
                <th className="w-24">Gateway</th>
                <th className="w-16">Port</th>
                <th className="w-36">From</th>
                <th className="w-36">Assigned</th>
                <th className="min-w-[400px] w-2/5">Message</th>
                <th className="w-20">User</th>
                <th className="w-48">Email</th>
                <th className="w-16">Type</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filteredMessages.map((msg, index) => (
                <tr key={index}>
                  <td className="text-xs">
                    {new Date(msg.timestamp).toLocaleString()}
                  </td>
                  <td className="text-xs">
                    {msg.port?.gateway?.name || msg.gateway_id || "?"}
                  </td>
                  <td className="text-xs">
                    {msg.port?.port_number || msg.receiver}
                  </td>
                  <td className="text-xs">{msg.sender}</td>
                  <td className="text-xs">
                    {msg.port?.assigned_number || "N/A"}
                  </td>
                  <td className="whitespace-pre-wrap">{msg.message}</td>
                  <td className="text-xs">{msg.port?.user_code || "N/A"}</td>
                  <td className="text-xs">{msg.port?.user_email || "N/A"}</td>
                  <td>
                    <span
                      className={`badge ${
                        msg.is_outgoing ? "bg-primary" : "bg-success"
                      }`}
                    >
                      {msg.is_outgoing ? "Out" : "In"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProtectedPage;